import type { ArticleResponse, Entry, SearchFilters, SearchResponse } from '../types'
import { apiFetch } from './client'
import { config } from '../config'

export type SortOrder =
  | 'score'
  | 'created_at' | '-created_at'
  // 2026-04-21〜 PLAN-home-dashboard-redesign:
  | 'sourced_count' | '-sourced_count'     // Article-only (/api/articles/search)
  | 'stale_by' | '-stale_by'               // Article-only
  | 'review_by' | '-review_by'             // Article-only
  | 'source_project' | '-source_project'   // 全 endpoint

export interface SearchOptions {
  sort?: SortOrder
}

// ===== Typed API client 層 (spec/overview/component-ownership.md) =====
//
// public path ごとに allowed filter が決まっている (spec/components/worker-facade/public-api-routing.md):
// - /api/search          (cross-concept): q, source_project, source_medium, since, until, mode, sort, subjectivity, limit, offset, aggregate
// - /api/entries/search  (Entry-only):    + origin ∈ human|auto, orphan, source_tool
// - /api/articles/search (Article-only):  + stale, include_stage, include_stale
//
// 境界錯誤を型レベルで防ぐため、filter 型を 3 分割する。
// dispatcher は UI の filters.origin を見て concept endpoint に振り分ける。

// ----- Filter 型 -----

// PLAN-cockpit-domain-mode (2026-05-07〜): 共通 Domain filter
interface DomainFilterFields {
  domain?: import('../types').Domain
  domain_mode?: import('../types').DomainMode
  scope?: 'global'
  include_sensitive?: boolean
  include_uncategorized?: boolean
}

/** 横断検索用 filter (concept-specific は受け付けない) */
export interface KnowledgeFilters extends DomainFilterFields {
  q?: string
  subjectivity?: import('../types').Subjectivity
  source_project?: string
  source_medium?: string
  since?: string
  until?: string
}

/** Entry-only filter */
export interface EntryFilters extends DomainFilterFields {
  q?: string
  origin?: 'human' | 'auto'  // synthesized は受け付けない
  subjectivity?: import('../types').Subjectivity
  source_project?: string
  source_medium?: string
  source_tool?: string
  orphan?: boolean
  since?: string
  until?: string
}

/** Article-only filter */
export interface ArticleFilters extends DomainFilterFields {
  q?: string
  subjectivity?: import('../types').Subjectivity
  source_project?: string
  source_medium?: string
  stale?: boolean
  include_stage?: string
  include_stale?: boolean
  since?: string
  until?: string
  // PLAN-article-feedback-layer + PLAN-judgment-cockpit (2026-05-15)
  has_decision?: boolean
  decision_confidence?: 'high' | 'medium' | 'low'
  decision_escalation?: boolean
  x_status?: 'present' | 'all_rejected' | 'not_applicable'
  feedback_state?: 'unreviewed' | 'concern' | 'reuse_ok' | 'reuse_ng'
  reuse_boost?: boolean
  // PLAN-judgment-cockpit Phase 2 (2026-05-15)
  text_schema_status?: 'primary_axis' | 'context_only' | 'weak_context' | 'invalid_text_schema' | 'not_article'
  review_overdue?: boolean
}

/** Entry-only filter, Linked Observations 取得用 */
export interface LinkedObservationFilters {
  source_article_id: string
  limit?: number
}

// ----- search 関数群 -----

// PLAN-cockpit-domain-mode (2026-05-07〜): Domain filter を query に展開する helper
function domainQuery(f: DomainFilterFields): Record<string, string | undefined> {
  return {
    domain: f.domain,
    domain_mode: f.domain_mode,
    scope: f.scope,
    include_sensitive: f.include_sensitive ? 'true' : undefined,
    include_uncategorized: f.include_uncategorized ? 'true' : undefined,
  }
}

export async function searchKnowledge(filters: KnowledgeFilters, limit = 50, offset = 0, signal?: AbortSignal, options: SearchOptions = {}): Promise<SearchResponse> {
  return apiFetch<SearchResponse>('/api/search', {
    query: {
      q: filters.q,
      subjectivity: filters.subjectivity,
      source_project: filters.source_project,
      source_medium: filters.source_medium,
      since: filters.since,
      until: filters.until,
      mode: config.searchMode,
      sort: options.sort,
      ...domainQuery(filters),
      limit,
      offset,
    },
    signal,
  })
}

export async function searchEntries(filters: EntryFilters, limit = 50, offset = 0, signal?: AbortSignal, options: SearchOptions = {}): Promise<SearchResponse> {
  return apiFetch<SearchResponse>('/api/entries/search', {
    query: {
      q: filters.q,
      origin: filters.origin,
      subjectivity: filters.subjectivity,
      source_project: filters.source_project,
      source_medium: filters.source_medium,
      source_tool: filters.source_tool,
      orphan: filters.orphan ? 'true' : undefined,
      since: filters.since,
      until: filters.until,
      mode: config.searchMode,
      sort: options.sort,
      ...domainQuery(filters),
      limit,
      offset,
    },
    signal,
  })
}

export async function searchArticles(filters: ArticleFilters, limit = 50, offset = 0, signal?: AbortSignal, options: SearchOptions = {}): Promise<SearchResponse> {
  return apiFetch<SearchResponse>('/api/articles/search', {
    query: {
      q: filters.q,
      subjectivity: filters.subjectivity,
      source_project: filters.source_project,
      source_medium: filters.source_medium,
      stale: filters.stale ? 'true' : undefined,
      include_stage: filters.include_stage,
      include_stale: filters.include_stale ? 'true' : undefined,
      since: filters.since,
      until: filters.until,
      mode: config.searchMode,
      sort: options.sort,
      ...domainQuery(filters),
      // PLAN-article-feedback-layer + PLAN-judgment-cockpit (2026-05-15)
      has_decision: filters.has_decision ? 'true' : undefined,
      decision_confidence: filters.decision_confidence,
      decision_escalation: filters.decision_escalation ? 'true' : undefined,
      x_status: filters.x_status,
      feedback_state: filters.feedback_state,
      reuse_boost: filters.reuse_boost ? 'true' : undefined,
      // PLAN-judgment-cockpit Phase 2 (2026-05-15)
      text_schema_status: filters.text_schema_status,
      review_overdue: filters.review_overdue ? 'true' : undefined,
      limit,
      offset,
    },
    signal,
  })
}

// PLAN-judgment-cockpit Phase 0.5 (2026-05-15): Linked Observations 取得
// 指定 Article への観察として記録された Entry 一覧 (entry_search?source_article_id=...)
export async function searchLinkedObservations(filters: LinkedObservationFilters, signal?: AbortSignal): Promise<SearchResponse> {
  return apiFetch<SearchResponse>('/api/entries/search', {
    query: {
      source_article_id: filters.source_article_id,
      sort: '-created_at',
      limit: filters.limit ?? 50,
    },
    signal,
  })
}

// ----- count 関数群 (dashboard / health strip 用、limit=1 で total のみ参照) -----

export async function countKnowledge(filters: KnowledgeFilters, signal?: AbortSignal): Promise<number> {
  const res = await searchKnowledge(filters, 1, 0, signal)
  return res.total
}

export async function countEntries(filters: EntryFilters, signal?: AbortSignal): Promise<number> {
  const res = await searchEntries(filters, 1, 0, signal)
  return res.total
}

export async function countArticles(filters: ArticleFilters, signal?: AbortSignal): Promise<number> {
  const res = await searchArticles(filters, 1, 0, signal)
  return res.total
}

// ----- Dispatcher: SearchFilters (UI state) から concept endpoint に振り分け -----
// UI の origin filter を見て searchEntries / searchArticles / searchKnowledge を選択する。
// 既存 UI は `filters.origin` を URL state / dispatcher signal として使い続ける方針 (spec/components/page-cockpit/index.md 参照)。
// PLAN-cockpit-domain-mode (2026-05-07〜): Domain Layer fields も透過する
export async function search(filters: SearchFilters, limit = 50, offset = 0, signal?: AbortSignal, options: SearchOptions = {}): Promise<SearchResponse> {
  // Domain Layer fields は全 endpoint に透過
  const domainFields: DomainFilterFields = {
    domain: filters.domain,
    domain_mode: filters.domain_mode,
    scope: filters.scope,
    include_sensitive: filters.include_sensitive,
    include_uncategorized: filters.include_uncategorized,
  }

  if (filters.origin === 'synthesized') {
    return searchArticles({
      q: filters.q,
      subjectivity: filters.subjectivity,
      source_project: filters.source_project,
      stale: filters.stale,
      include_stage: filters.include_stage,
      since: filters.since,
      until: filters.until,
      ...domainFields,
    }, limit, offset, signal, options)
  }
  if (filters.origin === 'human' || filters.origin === 'auto') {
    return searchEntries({
      q: filters.q,
      origin: filters.origin,
      subjectivity: filters.subjectivity,
      source_project: filters.source_project,
      orphan: filters.orphan,
      since: filters.since,
      until: filters.until,
      ...domainFields,
    }, limit, offset, signal, options)
  }
  // origin 未指定だが orphan / stale は concept-specific — orphan→entries、stale→articles
  if (filters.orphan) {
    return searchEntries({
      q: filters.q,
      subjectivity: filters.subjectivity,
      source_project: filters.source_project,
      orphan: true,
      since: filters.since,
      until: filters.until,
      ...domainFields,
    }, limit, offset, signal, options)
  }
  if (filters.stale) {
    return searchArticles({
      q: filters.q,
      subjectivity: filters.subjectivity,
      source_project: filters.source_project,
      stale: true,
      include_stage: filters.include_stage,
      since: filters.since,
      until: filters.until,
      ...domainFields,
    }, limit, offset, signal, options)
  }
  // 真の横断検索
  return searchKnowledge({
    q: filters.q,
    subjectivity: filters.subjectivity,
    source_project: filters.source_project,
    since: filters.since,
    until: filters.until,
    ...domainFields,
  }, limit, offset, signal, options)
}

// 集計: source_project 別件数
export interface AggregateBucket {
  key: string | null
  count: number
}

// PLAN-cockpit-domain-mode Phase 2b (2026-05-07〜): Domain filter 透過
export async function aggregateBySourceProject(
  signal?: AbortSignal,
  domainFilter?: { domain?: string; domain_mode?: 'strict' | 'broad' | 'exploratory' },
): Promise<AggregateBucket[]> {
  const res = await apiFetch<{ buckets: AggregateBucket[] }>('/api/search', {
    query: {
      aggregate: 'source_project',
      ...(domainFilter?.domain ? { domain: domainFilter.domain } : {}),
      ...(domainFilter?.domain_mode ? { domain_mode: domainFilter.domain_mode } : {}),
    },
    signal
  })
  return res.buckets ?? []
}

// 集計: origin × subjectivity (Home Dashboard マトリクス用)
export interface OriginSubjectivityBucket {
  origin: string
  subjectivity: string
  count: number
}

export async function aggregateByOriginSubjectivity(signal?: AbortSignal): Promise<OriginSubjectivityBucket[]> {
  const res = await apiFetch<{ buckets: OriginSubjectivityBucket[] }>('/api/search', {
    query: { aggregate: 'origin_subjectivity' },
    signal
  })
  return res.buckets ?? []
}

export async function getEntry(id: string, signal?: AbortSignal): Promise<Entry> {
  return apiFetch<Entry>(`/api/entries/${encodeURIComponent(id)}`, { signal })
}

export async function getArticle(id: string, signal?: AbortSignal): Promise<ArticleResponse> {
  return apiFetch<ArticleResponse>(`/api/articles/${encodeURIComponent(id)}`, { signal })
}

// バージョンチェーン: 最新版から previous_version を辿って全版を返す
// 注意: getArticle(latest_id) は最新版に resolve するので、各旧版を取るには Entry API を使う
export async function getArticleVersionChain(latestId: string, signal?: AbortSignal): Promise<import('../types').Article[]> {
  // 最新版を取得 (このとき previous_version が含まれる)
  const latest = await getArticle(latestId, signal)
  const chain: import('../types').Article[] = [latest.article]
  let prevId = latest.article.previous_version
  // previous_version を辿って各版を取得 (各版は最新 resolve しないよう /api/entries 経由)
  while (prevId) {
    try {
      const prev = await apiFetch<import('../types').Article>(`/api/entries/${encodeURIComponent(prevId)}`, { signal })
      chain.push(prev)
      prevId = prev.previous_version ?? null
    } catch {
      break
    }
  }
  return chain
}

// PATCH body: visibility は top-level、confidence/applicability/lifecycle_stage は article_meta 配下
// (spec/components/page-cockpit/editing-and-visibility.md)
// PLAN-sensitive-domain-visibility (2026-05-07〜): sensitive Domain Article の public-* 遷移は
// confirm_sensitive_publish=true + reason 必須 (worker-article で enforce 済、principles.md §3.18)
export interface ArticlePatchBody {
  visibility?: string
  confirm_sensitive_publish?: boolean
  reason?: string
  article_meta?: {
    confidence?: string
    applicability?: string
    lifecycle_stage?: string
  }
}

export async function patchArticle(id: string, body: ArticlePatchBody): Promise<{ patched_fields: string[] }> {
  return apiFetch<{ patched_fields: string[] }>(`/api/articles/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body
  })
}

// graph.json 取得 (NetworkX node-link 形式)
export interface GraphNode {
  id: string
  type?: string
  title?: string
  origin?: string
  created_at?: string
  source_project?: string
  subjectivity?: string
  lifecycle_stage?: string
  // graph.json contract (spec/components/sync-graphify/graph-json-contract.md):
  // id は graphify-internal で UUID とは限らない。KDB item は kdb_id (完全 UUID) で解決し、
  // 無ければ source_file から UUID を抽出する。concept/paper 等は kdb_id を持たない。
  kind?: string          // entry | article | concept | paper | doc | external
  kdb_id?: string        // KDB-backed node の完全 UUID (解決キー)
  source_file?: string   // 抽出元ファイル (entries/.. or articles/.. に UUID を含む)
  [key: string]: unknown
}

export interface GraphLink {
  source: string
  target: string
  kind?: string
  [key: string]: unknown
}

export interface GraphData {
  nodes: GraphNode[]
  links: GraphLink[]
}

export async function getGraph(signal?: AbortSignal): Promise<GraphData> {
  return apiFetch<GraphData>('/api/graph', { signal })
}

// ===== PLAN-entry-article-pc-contract Phase B: Article Auto-suggestion =====

export interface SuggestionSourceEntry {
  id: string
  title: string
  one_line: string | null
  subjectivity: string | null
}

export interface ArticleSuggestion {
  id: string
  cluster_key: string
  entry_ids: string[]
  proposed_title: string | null
  proposed_one_line: string | null
  proposed_theme: string | null
  proposed_applicability: string | null
  proposed_confidence: string | null
  draft_method: string | null
  status: 'pending' | 'approved' | 'rejected' | 'cooled'
  rejected_reason: string | null
  approved_article_id: string | null
  cooldown_until: string | null
  created_at: string
  updated_at: string
  source_entries: SuggestionSourceEntry[]
}

export async function listArticleSuggestions(
  limit = 10,
  signal?: AbortSignal,
  domain?: string | null,
): Promise<{ total: number; suggestions: ArticleSuggestion[] }> {
  // PLAN-cockpit-domain-mode Phase 2b (2026-05-07〜): domain 指定で article_suggestions.domain 絞込
  return apiFetch<{ total: number; suggestions: ArticleSuggestion[] }>(
    '/api/article-suggestions',
    { query: { limit: String(limit), ...(domain ? { domain } : {}) }, signal },
  )
}

export async function getArticleSuggestion(id: string, signal?: AbortSignal): Promise<ArticleSuggestion> {
  return apiFetch<ArticleSuggestion>(`/api/article-suggestions/${encodeURIComponent(id)}`, { signal })
}

export async function rejectArticleSuggestion(id: string, reason: string | null): Promise<{ ok: boolean; cooldown_days: number }> {
  return apiFetch<{ ok: boolean; cooldown_days: number }>(
    `/api/article-suggestions/${encodeURIComponent(id)}/reject`,
    { method: 'POST', body: { reason } },
  )
}

export async function approveArticleSuggestion(id: string, articleId: string): Promise<{ ok: boolean; article_id: string }> {
  return apiFetch<{ ok: boolean; article_id: string }>(
    `/api/article-suggestions/${encodeURIComponent(id)}/approve`,
    { method: 'POST', body: { article_id: articleId } },
  )
}

export async function runArticleSuggestionDetection(): Promise<{ ok: boolean; total_orphan_entries: number; total_clusters: number; new_suggestions: number; updated_suggestions: number }> {
  return apiFetch<{ ok: boolean; total_orphan_entries: number; total_clusters: number; new_suggestions: number; updated_suggestions: number }>(
    `/api/article-suggestions/run`,
    { method: 'POST' },
  )
}

// ===== PLAN-event-log-d1: 明示的 view event 記録 =====

/**
 * Article 表示時に view event を明示的に記録する。
 * 〜2026-04-30 は GET /api/entries/:id で auto-emit されていたが、graph 描画 / preview 等の
 * programmatic fetch でも view が量産されていた問題があり、明示 emit に切替。
 *
 * 失敗しても本流の表示は継続する (best-effort signal、spec/core/concept-signals.md)。
 */
export async function emitView(articleId: string): Promise<void> {
  try {
    await apiFetch<{ event_id: string; occurred_at: string }>(
      '/api/events',
      { method: 'POST', body: { event_type: 'view', article_id: articleId } },
    )
  } catch {
    // best-effort: view tracking 失敗は無視
  }
}

// ===== PLAN-cockpit-domain-mode Phase 3-4: Domain Layer endpoints =====

export interface DomainOverlay {
  item_type: 'entry' | 'article'
  item_id: string
  declared_domains: string[]
  curated_domains: Array<{
    domain: string
    polarity: 'include' | 'exclude'
    reason: string | null
    updated_by: string | null
    updated_at: string
  }>
  inferred_domains: Array<{
    domain: string
    confidence: number | null
    source: string | null
  }>
}

/**
 * Domain overlay 取得 (PLAN-cockpit-domain-mode Phase 3a)
 */
export async function getDomainOverlay(
  itemType: 'entry' | 'article',
  itemId: string,
  signal?: AbortSignal,
): Promise<DomainOverlay> {
  return apiFetch<DomainOverlay>(
    `/api/domains/${itemType}/${encodeURIComponent(itemId)}`,
    { signal },
  )
}

export interface DomainOverlayPatch {
  set?: Array<{
    domain: string
    polarity?: 'include' | 'exclude'
    reason?: string
    source?: string
  }>
  remove?: Array<{
    domain: string
    polarity?: 'include' | 'exclude'
  }>
}

/**
 * curated overlay PATCH (PLAN-cockpit-domain-mode Phase 3b)
 * polarity='exclude' の追加時は reason 必須
 */
export async function patchDomainOverlay(
  itemType: 'entry' | 'article',
  itemId: string,
  body: DomainOverlayPatch,
): Promise<{ ok: boolean; curated_domains: DomainOverlay['curated_domains'] }> {
  return apiFetch<{ ok: boolean; curated_domains: DomainOverlay['curated_domains'] }>(
    `/api/domains/${itemType}/${encodeURIComponent(itemId)}`,
    { method: 'PATCH', body: body as unknown as Record<string, unknown> },
  )
}

/**
 * Domain queue 集約 (PLAN-cockpit-domain-mode Phase 4)
 * Domain mode 時に Article suggestion + agent_task を集約取得
 */
export interface DomainQueueResponse {
  domain: string | null
  type: string
  article_suggestions?: Array<{
    id: string
    cluster_key: string
    proposed_title: string | null
    proposed_one_line: string | null
    proposed_confidence: string | null
    status: string
    domain: string | null
    created_at: string
  }>
  agent_tasks?: Array<{
    id: string
    domain: string
    workflow_id: string
    task_type: string
    status: string
    priority: number
    run_after: string
    attempt_count: number
    max_attempts: number
    created_at: string
  }>
}

export async function getDomainQueues(
  domain?: string | null,
  type: 'all' | 'article_suggestion' | 'agent_task' = 'all',
  signal?: AbortSignal,
): Promise<DomainQueueResponse> {
  const query: Record<string, string | undefined> = { type }
  if (domain) query.domain = domain
  return apiFetch<DomainQueueResponse>('/api/domain-queues', { query, signal })
}

// ===== Operational Data CRUD (PLAN-domain-* Phase 2、2026-05-07〜) =====

export interface OperationalDataResponse<T = Record<string, unknown>> {
  dataset: string
  total: number
  rows: T[]
}

export interface HealthDailyLog {
  log_date: string                  // ISO 8601 date (YYYY-MM-DD)
  sleep_hours: number | null
  sleep_quality: number | null
  bedtime: string | null
  wake_time: string | null
  steps: number | null
  exercise_minutes: number | null
  exercise_type: string | null
  weight_kg: number | null
  resting_hr: number | null
  notes: string | null
  source: string | null
  ingested_at: string
  task_id: string | null
}

export async function listOperationalData<T = Record<string, unknown>>(
  dataset: string,
  opts: { limit?: number; since?: string; until?: string } = {},
  signal?: AbortSignal,
): Promise<OperationalDataResponse<T>> {
  const query: Record<string, string | undefined> = {}
  if (opts.limit) query.limit = String(opts.limit)
  if (opts.since) query.since = opts.since
  if (opts.until) query.until = opts.until
  return apiFetch<OperationalDataResponse<T>>(`/api/operational-data/${encodeURIComponent(dataset)}`, { query, signal })
}

export async function upsertOperationalData<T = Record<string, unknown>>(
  dataset: string,
  body: Record<string, unknown>,
): Promise<{ ok: boolean; dataset: string; row: T; sensitive: boolean }> {
  return apiFetch<{ ok: boolean; dataset: string; row: T; sensitive: boolean }>(
    `/api/operational-data/${encodeURIComponent(dataset)}`,
    { method: 'POST', body },
  )
}

export async function deleteOperationalData(
  dataset: string,
  pk: string,
): Promise<{ ok: boolean; deleted: number; dataset: string; pk: string; sensitive: boolean }> {
  return apiFetch<{ ok: boolean; deleted: number; dataset: string; pk: string; sensitive: boolean }>(
    `/api/operational-data/${encodeURIComponent(dataset)}/${encodeURIComponent(pk)}`,
    { method: 'DELETE' },
  )
}
